// src/pages/WelcomePage.js
// Post-purchase welcome page for guest checkout users
// Fetches Stripe customer info to pre-populate sign-up, themed to match app

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useUser, useAuth, SignIn, SignUp } from '@clerk/clerk-react';
import * as Sentry from "@sentry/react";
import { joinPod, fetchSiteContent } from '../services/firebase';

function WelcomePage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { isLoaded, isSignedIn, user } = useUser();
    const { getToken } = useAuth();
    const [showAuth, setShowAuth] = useState(false);
    const [mode, setMode] = useState('signup');
    const [claiming, setClaiming] = useState(false);
    const [claimError, setClaimError] = useState('');
    const [customerInfo, setCustomerInfo] = useState(null);
    const [infoLoaded, setInfoLoaded] = useState(false);
    const hasClaimed = useRef(false);
    const [clerkUnavailable, setClerkUnavailable] = useState(false);
    const [siteConfig, setSiteConfig] = useState({});

    const sessionId = searchParams.get('session_id');
    const programSlug = searchParams.get('program');
    const podId = searchParams.get('pod');
    const redirectTo = programSlug ? `/program/${programSlug}` : '/dashboard';

    // Fetch customer info from Stripe BEFORE showing sign-up form
    useEffect(() => {
        if (!sessionId) { setInfoLoaded(true); return; }
        fetch(`/api/checkout-info?session_id=${sessionId}`)
            .then((r) => r.json())
            .then((data) => {
                if (data.firstName || data.email) {
                    setCustomerInfo(data);
                }
            })
            .catch(() => { })
            .finally(() => setInfoLoaded(true));
    }, [sessionId]);

    // Load siteConfig for contact email
    useEffect(() => {
        fetchSiteContent().then((c) => setSiteConfig(c || {})).catch(() => {});
    }, []);

    // When user signs in/up, claim their purchase and redirect
    useEffect(() => {
        if (!isLoaded || !isSignedIn || !user || !sessionId) return;
        if (hasClaimed.current) return;
        hasClaimed.current = true;

        const claimPurchase = async () => {
            setClaiming(true);
            try {
                const token = await getToken();
                const res = await fetch('/api/claim-purchase', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                    body: JSON.stringify({
                        sessionId,
                        userId: user.id,
                    }),
                });
                const data = await res.json();
                if (data.success) {
                    const claimPodId = data.podId || podId;
                    if (claimPodId) {
                        try {
                            await joinPod(claimPodId, user.id);
                        } catch (e) { Sentry.captureException(e, { tags: { feature: 'pod-join-post-claim' }, extra: { podId: claimPodId, userId: user.id } }); console.error('Auto-join pod failed:', e); }
                    }
                    await user.reload();
                    navigate(redirectTo, { replace: true });
                } else {
                    Sentry.captureMessage(`Purchase claim failed: ${data.error}`, { level: 'error', tags: { feature: 'claim-purchase' }, extra: { sessionId, userId: user.id } });
                    console.error('Claim failed:', data.error);
                    setClaimError('There was an issue linking your purchase. Please contact support.');
                    setClaiming(false);
                }
            } catch (err) {
                Sentry.captureException(err, { tags: { feature: 'claim-purchase' }, extra: { sessionId, userId: user.id } });
                console.error('Claim error:', err);
                setClaimError('There was an issue linking your purchase. Please contact support.');
                setClaiming(false);
            }
        };

        claimPurchase();
    }, [isLoaded, isSignedIn, user, sessionId, redirectTo, navigate, podId]);

    // Show auth form once customer info is loaded (so initialValues are ready)
    useEffect(() => {
        if (infoLoaded) {
            setShowAuth(true);
        }
    }, [infoLoaded]);

    // Detect if Clerk auth forms fail to load (Clerk outage)
    useEffect(() => {
        if (!showAuth || isLoaded) return;
        const timer = setTimeout(() => {
            const clerkLoaded = window.Clerk && window.Clerk.loaded;
            if (!clerkLoaded && !isLoaded) setClerkUnavailable(true);
        }, 15000);
        return () => clearTimeout(timer);
    }, [showAuth, isLoaded]);

    // Claiming state
    if (claiming) {
        return (
            <div style={{
                minHeight: '100vh', background: 'var(--mom-cream)',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                padding: '40px 24px',
            }}>
                <div style={{
                    width: 48, height: 48, borderRadius: '50%',
                    border: '3px solid var(--mom-cream-dark)', borderTopColor: 'var(--mom-primary)',
                    animation: 'spin 0.8s linear infinite',
                }} />
                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 16, color: 'var(--mom-warm-gray)', marginTop: 20 }}>
                    Setting up your account...
                </p>
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
        );
    }

    // Build redirect URL preserving all params
    const authRedirectUrl = `/welcome?session_id=${sessionId || ''}&program=${programSlug || ''}${podId ? `&pod=${podId}` : ''}`;

    return (
        <div style={{
            minHeight: '100vh', background: 'var(--mom-cream)',
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'flex-start', padding: '60px 24px 40px',
        }}>
            {/* Success message */}
            <div className="slide-up" style={{ textAlign: 'center', marginBottom: 32, maxWidth: 480 }}>
                <div style={{
                    width: 64, height: 64, borderRadius: '50%',
                    background: 'linear-gradient(135deg, var(--mom-primary), var(--mom-primary-light))',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    margin: '0 auto 24px',
                    boxShadow: '0 8px 25px color-mix(in srgb, var(--mom-primary) 30%, transparent)',
                }}>
                    <span style={{ fontSize: 28, color: 'white' }}>✓</span>
                </div>
                <h1 style={{
                    fontFamily: 'var(--mom-font-serif)', fontSize: 'clamp(28px, 5vw, 36px)',
                    fontStyle: 'italic', fontWeight: 700, color: 'var(--mom-charcoal)',
                    marginBottom: 12, lineHeight: 1.2,
                }}>
                    You're in!
                </h1>
                <p style={{
                    fontFamily: 'var(--mom-font-sans)', fontSize: 16,
                    color: 'var(--mom-warm-gray)', lineHeight: 1.6, maxWidth: 400, margin: '0 auto',
                }}>
                    Your purchase was successful. {mode === 'signup' ? 'Create your account' : 'Sign in'} below to access your program.
                </p>
                {claimError && (
                    <p style={{
                        fontFamily: 'var(--mom-font-sans)', fontSize: 14,
                        color: 'var(--mom-danger)', marginTop: 16,
                    }}>{claimError}</p>
                )}
            </div>

            {/* Clerk auth */}
            {showAuth && !claiming && (
                <div className="slide-up stagger-1" style={{ width: '100%', maxWidth: 440, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    {clerkUnavailable ? (
                        /* ── Clerk is down — reassuring fallback ── */
                        <div style={{
                            background: 'rgba(255,255,255,0.55)', backdropFilter: 'blur(20px) saturate(1.4)',
                            WebkitBackdropFilter: 'blur(20px) saturate(1.4)',
                            borderRadius: 24, border: '1px solid rgba(255,255,255,0.55)',
                            padding: '36px 32px', textAlign: 'center', width: '100%',
                            boxShadow: '0 12px 40px rgba(0,0,0,0.08)',
                        }}>
                            <div style={{
                                width: 48, height: 48, borderRadius: '50%',
                                background: 'linear-gradient(135deg, var(--mom-primary), var(--mom-primary-dark))',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                margin: '0 auto 20px',
                            }}>
                                <span style={{ fontSize: 22, color: 'white' }}>♡</span>
                            </div>
                            <h2 style={{
                                fontFamily: 'var(--mom-font-serif)', fontSize: 22, fontStyle: 'italic',
                                color: 'var(--mom-charcoal)', marginBottom: 12,
                            }}>
                                Your purchase is safe!
                            </h2>
                            <p style={{
                                fontFamily: 'var(--mom-font-sans)', fontSize: 15, color: 'var(--mom-warm-gray)',
                                lineHeight: 1.7, marginBottom: 20,
                            }}>
                                We're experiencing a brief issue with account creation, but don't worry — your purchase has been recorded and is waiting for you.
                            </p>
                            <div style={{
                                background: 'var(--mom-cream)', borderRadius: 14, padding: '20px 24px',
                                textAlign: 'left', marginBottom: 20,
                            }}>
                                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, fontWeight: 600, color: 'var(--mom-charcoal)', marginBottom: 10 }}>
                                    Here's what to do:
                                </p>
                                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: 'var(--mom-charcoal)', lineHeight: 1.7, marginBottom: 8 }}>
                                    <span style={{ color: 'var(--mom-primary)', marginRight: 8 }}>1.</span>
                                    Try refreshing this page in a few minutes
                                </p>
                                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: 'var(--mom-charcoal)', lineHeight: 1.7, marginBottom: 8 }}>
                                    <span style={{ color: 'var(--mom-primary)', marginRight: 8 }}>2.</span>
                                    Or come back later and sign up using <strong>the same email you used at checkout</strong> — your program will be linked automatically
                                </p>
                                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 14, color: 'var(--mom-charcoal)', lineHeight: 1.7 }}>
                                    <span style={{ color: 'var(--mom-primary)', marginRight: 8 }}>3.</span>
                                    If you need help, email <a href={`mailto:${siteConfig.contactEmail || 'hello@daniellenbarr.com'}`} style={{ color: 'var(--mom-link)', fontWeight: 600 }}>{siteConfig.contactEmail || 'hello@daniellenbarr.com'}</a>
                                </p>
                            </div>
                            <button
                                onClick={() => window.location.reload()}
                                className="mom-btn mom-btn-primary"
                                style={{ padding: '14px 36px', fontSize: 15, width: '100%' }}
                            >
                                Try Again
                            </button>
                        </div>
                    ) : (
                        /* ── Normal Clerk auth forms ── */
                        <>
                            {mode === 'signup' ? (
                                <SignUp

                                    initialValues={{
                                        firstName: customerInfo?.firstName || '',
                                        lastName: customerInfo?.lastName || '',
                                        emailAddress: customerInfo?.email || '',
                                    }}
                                    afterSignUpUrl={authRedirectUrl}
                                />
                            ) : (
                                <SignIn

                                    initialValues={{
                                        identifier: customerInfo?.email || '',
                                    }}
                                    afterSignInUrl={authRedirectUrl}
                                />
                            )}

                            <button
                                onClick={() => setMode(mode === 'signup' ? 'signin' : 'signup')}
                                style={{
                                    marginTop: 20, background: 'none', border: 'none', cursor: 'pointer',
                                    fontFamily: 'var(--mom-font-sans)', fontSize: 14,
                                    color: 'var(--mom-primary)', fontWeight: 600,
                                }}
                            >
                                {mode === 'signup' ? 'Already have an account? Sign in' : 'Need an account? Sign up'}
                            </button>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

export default WelcomePage;